import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { getSchemeById } from '@/lib/colorSchemes'
import { useAppearanceStore } from '@/stores/appearance'

export function useThemeEffect() {
	const { colorSchemeId, themeMode } = useAppearanceStore()
	const [systemPrefersDark, setSystemPrefersDark] = useState(() =>
		typeof window !== 'undefined'
			? window.matchMedia('(prefers-color-scheme: dark)').matches
			: false
	)
	const prevColorSchemeId = useRef(colorSchemeId)
	const prevTheme = useRef<'light' | 'dark' | null>(null)

	// Listen for system theme preference changes
	useEffect(() => {
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
		const handleChange = (e: MediaQueryListEvent) => {
			setSystemPrefersDark(e.matches)
		}

		mediaQuery.addEventListener('change', handleChange)
		return () => mediaQuery.removeEventListener('change', handleChange)
	}, [])

	useLayoutEffect(() => {
		const root = document.documentElement
		// Resolve theme based on themeMode
		const theme = themeMode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : themeMode
		const scheme = getSchemeById(colorSchemeId) || getSchemeById('default')

		if (!scheme) return

		const colors = theme === 'dark' ? scheme.dark : scheme.light

		// Skip if nothing changed
		if (prevColorSchemeId.current === colorSchemeId && prevTheme.current === theme) {
			return
		}

		prevColorSchemeId.current = colorSchemeId
		prevTheme.current = theme

		// Apply dark class
		if (theme === 'dark') {
			root.classList.add('dark')
		} else {
			root.classList.remove('dark')
		}

		// =============================================
		// Apply all color scheme CSS variables
		// =============================================

		// Scheme variables (for reference)
		root.style.setProperty('--scheme-primary-hue', colors.primaryHue.toString())
		root.style.setProperty('--scheme-primary', colors.primary)
		root.style.setProperty('--scheme-secondary', colors.secondary)
		root.style.setProperty('--scheme-secondary-hue', colors.secondaryHue.toString())

		// Primary colors
		root.style.setProperty('--primary', colors.primary)
		root.style.setProperty('--primary-foreground', colors.primaryForeground)
		root.style.setProperty('--secondary', colors.secondary)
		root.style.setProperty('--secondary-foreground', colors.secondaryForeground)
		root.style.setProperty('--ring', colors.ring)

		// Background colors
		root.style.setProperty('--background', colors.background)
		root.style.setProperty('--foreground', colors.foreground)
		root.style.setProperty('--card', colors.card)
		root.style.setProperty('--card-foreground', colors.cardForeground)
		root.style.setProperty('--popover', colors.card)
		root.style.setProperty('--popover-foreground', colors.cardForeground)

		// Accent colors
		root.style.setProperty('--accent', colors.accent)
		root.style.setProperty('--accent-foreground', colors.accentForeground)
		root.style.setProperty('--muted', colors.muted)
		root.style.setProperty('--muted-foreground', colors.mutedForeground)

		// Border & input
		root.style.setProperty('--border', colors.border)
		root.style.setProperty('--input', colors.input)

		// Sidebar colors
		root.style.setProperty('--sidebar', colors.sidebar)
		root.style.setProperty('--sidebar-bg', colors.sidebarBg)
		root.style.setProperty('--sidebar-foreground', colors.sidebarForeground)
		root.style.setProperty('--sidebar-primary', colors.sidebarPrimary)
		root.style.setProperty('--sidebar-primary-foreground', colors.sidebarPrimaryForeground)
		root.style.setProperty('--sidebar-accent', colors.sidebarAccent)
		root.style.setProperty('--sidebar-accent-foreground', colors.sidebarAccentForeground)
		root.style.setProperty('--sidebar-border', colors.sidebarBorder)
		root.style.setProperty('--sidebar-ring', colors.sidebarRing)

		// Set darker shade for active sidebar menu item
		const hue = colors.primaryHue
		const cDark = 0.05
		const sidebarActive = `oklch(0.38 ${cDark} ${hue})`
		root.style.setProperty('--sidebar-primary-active', sidebarActive)

		// Update banner background color
		const cLight = 0.04
		const bannerBg = theme === 'dark' ? 'oklch(0.12 0 0)' : `oklch(0.15 ${cLight} ${hue})`
		root.style.setProperty('--banner-bg', bannerBg)

		// =============================================
		// Update primary color scale (for UI components)
		// =============================================
		root.style.setProperty('--color-primary-50', `oklch(0.97 0.01 ${hue})`)
		root.style.setProperty('--color-primary-100', `oklch(0.94 ${cLight * 0.5} ${hue})`)
		root.style.setProperty('--color-primary-200', `oklch(0.88 ${cLight * 0.75} ${hue})`)
		root.style.setProperty('--color-primary-300', `oklch(0.78 ${cLight} ${hue})`)
		root.style.setProperty('--color-primary-400', `oklch(0.60 ${cLight} ${hue})`)
		root.style.setProperty('--color-primary-500', colors.primary)
		root.style.setProperty('--color-primary-600', `oklch(0.38 ${cDark} ${hue})`)
		root.style.setProperty('--color-primary-700', `oklch(0.33 ${cDark} ${hue})`)
		root.style.setProperty('--color-primary-800', `oklch(0.28 ${cDark * 0.75} ${hue})`)
		root.style.setProperty('--color-primary-900', `oklch(0.24 ${cDark * 0.5} ${hue})`)
		root.style.setProperty('--color-primary-950', `oklch(0.18 ${cDark * 0.25} ${hue})`)

		// =============================================
		// Update Tailwind CSS theme variables
		// =============================================
		root.style.setProperty('--color-background', colors.background)
		root.style.setProperty('--color-foreground', colors.foreground)
		root.style.setProperty('--color-card', colors.card)
		root.style.setProperty('--color-card-foreground', colors.cardForeground)
		root.style.setProperty('--color-popover', colors.card)
		root.style.setProperty('--color-popover-foreground', colors.cardForeground)
		root.style.setProperty('--color-primary', colors.primary)
		root.style.setProperty('--color-primary-foreground', colors.primaryForeground)
		root.style.setProperty('--color-secondary', colors.secondary)
		root.style.setProperty('--color-secondary-foreground', colors.secondaryForeground)
		root.style.setProperty('--color-muted', colors.muted)
		root.style.setProperty('--color-muted-foreground', colors.mutedForeground)
		root.style.setProperty('--color-accent', colors.accent)
		root.style.setProperty('--color-accent-foreground', colors.accentForeground)
		root.style.setProperty('--color-border', colors.border)
		root.style.setProperty('--color-input', colors.input)
		root.style.setProperty('--color-ring', colors.ring)
		root.style.setProperty('--color-sidebar', colors.sidebar)
		root.style.setProperty('--color-sidebar-bg', colors.sidebarBg)
		root.style.setProperty('--color-sidebar-foreground', colors.sidebarForeground)
		root.style.setProperty('--color-sidebar-primary', colors.sidebarPrimary)
		root.style.setProperty('--color-sidebar-primary-foreground', colors.sidebarPrimaryForeground)
		root.style.setProperty('--color-sidebar-accent', colors.sidebarAccent)
		root.style.setProperty('--color-sidebar-accent-foreground', colors.sidebarAccentForeground)
		root.style.setProperty('--color-sidebar-border', colors.sidebarBorder)
		root.style.setProperty('--color-sidebar-ring', colors.sidebarRing)
	}, [colorSchemeId, themeMode, systemPrefersDark])
}
